// ============================================================================
// DEMO SEND GUARD in the browser (W-DEMO, DEMO-SPEC section 6, slice S4)
// ----------------------------------------------------------------------------
// A demo business holds made-up clients, and its addresses all end in the
// reserved .example domain so nothing can be delivered (DEC-AU-10). A visitor
// at the booth can still type a real address, and device memory can still
// hold one from a real business. So, for a demo business only:
//   - the quote email modal refuses to open a draft to any address that does
//     not end in .example (E2),
//   - the Assistant's compose_email_body refuses the same (E3),
//   - one capture-phase listener cancels any mailto: link to such an address
//     anywhere in the app (E4).
// Every check reads isDemoBusiness() at the moment it runs, so a real
// business (RJ Bird Building) is never touched.
//
// ALLOWED TEST RECIPIENTS (migration 126, Peter 2026-09-27). The sales team
// shows a real send to an internal inbox, so a demo business also allows the
// real addresses the QuoteMaker operator put on its list
// (getDemoAllowedRecipients, from /api/auth/me). An address is allowed when it
// ends in .example OR equals an entry on the list, ignoring case and outer
// spaces. A To or CC with any address that is neither is still refused, even
// when another address in it is allowed. Server sending is unchanged: SMTP
// still refuses every send for a demo business.
// ============================================================================

import { getDemoAllowedRecipients, isDemoBusiness } from './demo-business';

export const DEMO_SEND_REFUSED_EVENT = 'qm:demo-send-refused';

/** True for an address whose domain ends in the reserved .example top-level domain. */
export function isFictionEmailAddress(value: unknown): boolean {
    const address = String(value ?? '').trim().toLowerCase();
    return /^[^\s@<>,;"]+@(?:[a-z0-9-]+\.)+example$/.test(address);
}

/**
 * True when a demo business may open a draft to this address: it ends in
 * .example, or it equals an address on the business's allowed list (case
 * and outer spaces ignored). Never a partial match.
 */
export function isAllowedDemoRecipient(value: unknown): boolean {
    if (isFictionEmailAddress(value)) return true;
    const address = String(value ?? '').trim().toLowerCase();
    return address !== '' && getDemoAllowedRecipients().includes(address);
}

/** Splits a To or CC field (commas, semicolons, spaces) into addresses. */
export function splitAddressList(value: unknown): string[] {
    const list = Array.isArray(value) ? value : [value];
    return list
        .flatMap((entry) => String(entry ?? '').split(/[,;\s]+/))
        .map((entry) => entry.trim().replace(/^<|>$/g, ''))
        .filter(Boolean);
}

/** Every address in the lists that is neither a made-up .example address nor on the allowed list. */
export function realAddressesIn(...lists: unknown[]): string[] {
    return lists.flatMap((list) => splitAddressList(list)).filter((address) => !isAllowedDemoRecipient(address));
}

export function demoRealAddressMessage(addresses: string[]): string {
    const shown = addresses.slice(0, 3).join(', ');
    return `This is a demo business with made-up clients, so QuoteMaker only opens email to addresses ending in .example or on this business's allowed list. Change ${shown} to a made-up address.`;
}

/**
 * The sentence to show when a send must be refused, or null when it may go
 * ahead. Always null for a real business.
 */
export function demoSendRefusal(...lists: unknown[]): string | null {
    if (!isDemoBusiness()) return null;
    const real = realAddressesIn(...lists);
    return real.length ? demoRealAddressMessage(real) : null;
}

/** The To, CC and BCC addresses of a mailto: link. */
export function mailtoRecipients(href: string): string[] {
    const raw = String(href || '').replace(/^mailto:/i, '');
    const queryAt = raw.indexOf('?');
    const decode = (value: string) => {
        try { return decodeURIComponent(value); } catch { return value; }
    };
    const out = splitAddressList(decode(queryAt >= 0 ? raw.slice(0, queryAt) : raw));
    if (queryAt >= 0) {
        for (const pair of raw.slice(queryAt + 1).split('&')) {
            const [key, value = ''] = pair.split('=');
            if (/^(to|cc|bcc)$/i.test(decode(key || ''))) out.push(...splitAddressList(decode(value.replace(/\+/g, ' '))));
        }
    }
    return out;
}

function mailtoAnchorFrom(target: EventTarget | null): HTMLAnchorElement | null {
    let node = target as (Element | null);
    while (node && node.nodeType !== 1) node = (node as unknown as Node).parentNode as Element | null;
    const anchor = node?.closest?.('a[href]') as HTMLAnchorElement | null;
    return anchor && /^\s*mailto:/i.test(anchor.getAttribute('href') || '') ? anchor : null;
}

/**
 * Installs the one document-wide mailto: guard (E4). Returns the uninstall
 * function. `onRefused` receives the sentence; by default it is dispatched as
 * a `qm:demo-send-refused` window event, which the app shows as a toast.
 */
export function installDemoMailLinkGuard(
    doc: Document = document,
    { isDemo = isDemoBusiness, onRefused }: { isDemo?: () => boolean; onRefused?: (message: string) => void } = {},
): () => void {
    const handler = (event: Event) => {
        if (!isDemo()) return;
        const anchor = mailtoAnchorFrom(event.target);
        if (!anchor) return;
        const recipients = mailtoRecipients(anchor.getAttribute('href') || '');
        const real = recipients.filter((address) => !isAllowedDemoRecipient(address));
        if (!real.length) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const message = demoRealAddressMessage(real);
        if (onRefused) onRefused(message);
        else doc.defaultView?.dispatchEvent(new CustomEvent(DEMO_SEND_REFUSED_EVENT, { detail: { message } }));
    };
    doc.addEventListener('click', handler, true);
    doc.addEventListener('auxclick', handler, true);
    return () => {
        doc.removeEventListener('click', handler, true);
        doc.removeEventListener('auxclick', handler, true);
    };
}
